import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Rileva quando è stato deployato un nuovo build della SPA e permette di
 * applicarlo senza hard refresh.
 *
 * Come funziona:
 *  - al build, `__BUILD_ID__` viene iniettato nel bundle e lo stesso valore
 *    finisce in `dist/version.json` (servito come `/version.json`);
 *  - a runtime l'app fa polling di `/version.json` (cache-busted, no-store) e,
 *    se il `buildId` servito è diverso da quello con cui sta girando, vuol dire
 *    che è uscito un nuovo deploy → `updateAvailable = true`;
 *  - `applyUpdate()` ricarica la pagina (con pulizia best-effort della Cache
 *    API) per caricare il nuovo `index.html` e i nuovi bundle hashati.
 *
 * Nessun service worker coinvolto: l'unica risorsa "appiccicosa" è `index.html`,
 * e il reload — insieme agli header no-cache lato server (.htaccess) — basta a
 * prenderla fresca.
 */
const POLL_INTERVAL_MS = 60_000;
const INITIAL_DELAY_MS = 5_000;
const CURRENT_BUILD_ID = __BUILD_ID__;

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildId?: unknown };
    return typeof data.buildId === 'string' && data.buildId.length > 0 ? data.buildId : null;
  } catch {
    // offline / errore di rete: riproviamo al prossimo giro, nessun banner
    return null;
  }
}

export function useVersionCheck(): {
  updateAvailable: boolean;
  applyUpdate: () => Promise<void>;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    // In sviluppo non c'è un version.json reale da confrontare.
    if (CURRENT_BUILD_ID.startsWith('dev-')) return;

    const check = async (): Promise<void> => {
      if (done.current) return;
      const deployed = await fetchDeployedBuildId();
      if (deployed && deployed !== CURRENT_BUILD_ID) {
        done.current = true;
        setUpdateAvailable(true);
      }
    };

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };

    // Una volta rilevato l'aggiornamento, `done` fa sì che i tick successivi
    // siano no-op; l'interval viene fermato definitivamente alla cleanup.
    const initial = setTimeout(() => void check(), INITIAL_DELAY_MS);
    const interval = setInterval(() => void check(), POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(initial);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const applyUpdate = useCallback(async (): Promise<void> => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // best-effort: anche senza pulizia cache il reload prende l'index fresco
    }
    window.location.reload();
  }, []);

  return { updateAvailable, applyUpdate };
}
